import React, { ReactNode } from 'react';
import { AuthModal } from '@/components/auth/AuthModal';
import { useSignIn, useSignUp, useForgotPassword, useAuth } from '@/hooks/data/auth';
import { useAuthModal } from '@/stores/ui';

interface GlobalAuthModalProviderProps {
  children: ReactNode;
}

export const GlobalAuthModalProvider = React.memo(({ children }: GlobalAuthModalProviderProps) => {
  const signIn = useSignIn();
  const signUp = useSignUp();
  const forgot = useForgotPassword();
  const setUnverifiedEmail = useAuth((s) => s.setUnverifiedEmail);
  const { isAuthModalOpen, authModalMessage, hideAuthModal } = useAuthModal();

  const handleLogin = React.useCallback(async (emailOrUsername: string, password: string) => {
    try {
      await signIn.mutateAsync({ identifier: emailOrUsername, password });
      hideAuthModal();
    } catch (err: unknown) {
      const e = err as { code?: string; email?: string };
      // Store email for resend verification if login failed due to unverified email
      if (e.code === 'EMAIL_NOT_VERIFIED') {
        // If identifier is email, use it; otherwise try to get from error
        const isEmail = emailOrUsername.includes('@');
        const emailToStore = isEmail ? emailOrUsername : e.email;
        if (emailToStore) {
          setUnverifiedEmail(emailToStore.toLowerCase());
        }
      }
      throw err; // Re-throw to let mutation onError handle it
    }
  }, [signIn, hideAuthModal, setUnverifiedEmail]);

  const handleRegister = React.useCallback(async (email: string, username: string, password: string) => {
    await signUp.mutateAsync({ email, username, password });
  }, [signUp]);

  const handleForgot = React.useCallback(async (email: string) => {
    await forgot.mutateAsync({ email });
  }, [forgot]);

  return (
    <>
      {children}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={hideAuthModal}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onForgot={handleForgot}
        contextMessage={authModalMessage}
        isLoginLoading={signIn.isPending}
        isRegisterLoading={signUp.isPending}
        isForgotLoading={forgot.isPending}
      />
    </>
  );
});

